import { Component, OnInit, computed, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { mergeMap, tap } from 'rxjs';
import { InputComponent } from '../input/input.component';
import { MaskFieldInputComponent } from '../mask-field-input/mask-field-input.component';
import { IconComponent } from '../icons/icon.component';
import { RoundIconComponent } from '../round-icon/round-icon.component';
import { WalletEditorService } from '../../core/services/wallet-editor.service';
import { AuthService } from '../../core/services/auth.service';
import { ErrorsService } from '../../core/services/errors.service';
import { GiftCard } from '../../core/models/core/gift-card.model';
import { CreditCard } from '../../core/models/core/credit-card.model';
import { MaskFieldInputType } from '../mask-field-input/mask-field-input-type.enum';
import { IconNameType } from '../icons/icon-name-type.enum';
import { ModalService, ModalName } from '../../core/services/modal.service';
import { TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import { ToastService } from '../../core/services/toast.service';

@Component({
  selector: 'cwo-edit-wallet-modal',
  standalone: true,
  imports: [
    IconComponent,
    RoundIconComponent,
    InputComponent,
    MaskFieldInputComponent,
    FormsModule,
    ReactiveFormsModule,
    TranslocoDirective
  ],
  templateUrl: './edit-wallet-modal.component.html',
  styleUrl: './edit-wallet-modal.component.scss',
})
export class EditWalletModalComponent implements OnInit {
  authService = inject(AuthService);
  modalService = inject(ModalService);
  errorsService = inject(ErrorsService);
  walletEditorService = inject(WalletEditorService);
  translocoService = inject(TranslocoService);
  toastService = inject(ToastService);
  walletInfo = computed(() => this.walletEditorService.state());
  isCreditCard = computed(() => {
    return !!this.walletInfo()?.lastDigits;
  });

  editTitle = computed(() => {
    return this.isCreditCard()
      ? '****' + this.walletInfo()?.lastDigits
      : `"${this.walletInfo()?.friendlyName}"`;
  });

  editCardForm!: FormGroup;
  iconNameType = IconNameType;
  maskFieldInputType = MaskFieldInputType;

  showErrors = false;
  allErrors?: string[] = [];

  validationMessages: any = {
    expiration: {
      required: 'Exp. Date is required',
    },
    webBillingZip: {
      required: 'Billing Zip is required',
    },
    name: {
      required: 'Name is required',
    },
    friendlyName: {
      required: 'Friendly Name is required',
    },
    giftCardNumber: {
      required: 'Number is required',
    },
    pin: {
      required: 'Pin is required',
    },
  };

  ngOnInit(): void {
    this.editCardForm = new FormGroup({
      expiration: new FormControl(
        this.walletInfo()?.expiration,
        this.isCreditCard() ? Validators.compose([Validators.required]) : null
      ),
      webBillingZip: new FormControl(
        this.walletInfo()?.webBillingZip,
        this.isCreditCard() ? Validators.compose([Validators.required]) : null
      ),
      name: new FormControl(
        `${
          this.walletInfo()?.firstName ? this.walletInfo()?.firstName + ' ' : ''
        }${this.walletInfo()?.lastName || ''}`
      ),
      friendlyName: new FormControl(
        this.walletInfo()?.friendlyName,
        !this.isCreditCard() ? Validators.compose([Validators.required]) : null
      ),
    });
  }

  closeModal(event: Event) {
    event.preventDefault();
    this.modalService.closeModal();
  }

  onEditCardFormSubmit() {
    this.showErrors = false;
    this.allErrors = [];

    if (!this.editCardForm.valid) {
      this.showErrors = true;
      return;
    }

    if (this.isCreditCard()) {
      if (
        this.editCardForm.value.expiration &&
        this.editCardForm.value.webBillingZip
      ) {
        const newCreditCard = new CreditCard();
        newCreditCard.ordinal = this.walletInfo()?.ordinal;
        newCreditCard.cardType = this.walletInfo()?.cardType;
        newCreditCard.expiration = this.editCardForm.value.expiration.replace(
          /\D/g,
          ''
        );
        newCreditCard.lastDigits = this.walletInfo()?.lastDigits;
        newCreditCard.webBillingZip = this.editCardForm.value.webBillingZip;
        const nameParts = this.editCardForm.value.name.split(' ');
        newCreditCard.firstName = nameParts[0];
        newCreditCard.lastName = nameParts[1];

        this.authService
          .updateCreditCard(newCreditCard)
          .pipe(
             tap(() => {
                    this.toastService.showSuccess(
                      this.translocoService.translate('route.account.wallet.gift_card_updated')
                           );
                       }),
            mergeMap(() => this.authService.fetchCreditCards()))
          .subscribe({
            next: () => {
              this.modalService.closeModal(ModalName.EditWallet);
            },
            error: (err) => {
              if (!err.error) {
                this.allErrors = ['Network error'];
              } else {
                const parsedError = this.errorsService.parseErrorResponse(err);
                this.allErrors = parsedError.allErrors;
              }
            },
          });
      }
    } else {
      if (this.editCardForm.value.friendlyName) {
        const newGiftCard = new GiftCard();
        newGiftCard.friendlyName = this.editCardForm.value.friendlyName;
        newGiftCard.number = Number(this.walletInfo()?.number);
        newGiftCard.pin = this.walletInfo()?.pin;

        this.authService
          .updateGiftCard(newGiftCard)
          .pipe(
             tap(() => {
                    this.toastService.showSuccess(
                      this.translocoService.translate('route.account.wallet.gift_card_updated')
                           );
                       }),
            mergeMap(() => this.authService.fetchGiftCardsWrapper()))
          .subscribe({
            next: () => {
                this.modalService.closeModal(ModalName.EditWallet);
            },
            error: (err) => {
              if (!err.error) {
                this.allErrors = ['Network error'];
              } else {
                const parsedError = this.errorsService.parseErrorResponse(err);
                this.allErrors = parsedError.allErrors;
              }
            },
          });
      }
    }
  }
}
